import type { PropsWithChildren, ReactNode } from 'react';
import { useRef } from 'react';
import { Navigate, useLocation, useNavigation } from 'react-router';

import { ORIGIN } from '#src/config.ts';
import { useDefaultSportPages } from '#src/constants/routes.ts';
import { useSportContext } from '#src/contexts/sport/index.ts';
import { useAfterLoggedInTarget } from '#src/hooks/useAfterLoggedInTarget.ts';
import { useRedirectUrl } from '#src/hooks/useRedirectUrl.ts';
import { stripPrefix } from '#src/i18n/useTranslations.ts';

type Props = {
  currentUser?: {
    userSettings: { [key: string]: any };
    fromPath?: string | null;
  } | null;
};

const useRedirectToAfterSignIn = () => {
  const { sport } = useSportContext();
  const location = useLocation();
  const redirectUrl = useRedirectUrl();
  const afterLoggedInTarget = useAfterLoggedInTarget();
  const defaultSportPages = useDefaultSportPages(true);

  return (currentUser: Props['currentUser']) => {
    const defaultTo = defaultSportPages[sport];
    const to =
      redirectUrl ||
      currentUser?.fromPath ||
      afterLoggedInTarget ||
      location.pathname;

    // make sure we don't redirect to an external URL as it would be a security issue.
    const { pathname, search } = new URL(to, ORIGIN);
    return stripPrefix(`${pathname.replace(/^\/$/, '') || defaultTo}${search}`);
  };
};

export const RedirectAfterSignIn = ({
  currentUser,
  children,
}: PropsWithChildren<Props>): ReactNode => {
  const location = useLocation();
  const navigation = useNavigation();
  const isNavigating = !!navigation.location;
  /** We need to keep track of the redirection state between renders to avoid rendering children in case they have conflicting redirections. */
  const shouldRedirectState = useRef<'none' | 'redirect' | 'redirecting'>(
    currentUser ? 'none' : 'redirect'
  );
  const redirectToAfterSignIn = useRedirectToAfterSignIn()(currentUser);

  if (shouldRedirectState.current === 'redirecting') {
    if (isNavigating) {
      return null;
    }
    if (
      stripPrefix(
        `${location.pathname.replace(/^\/$/, '')}${location.search}`
      ) === redirectToAfterSignIn
    ) {
      shouldRedirectState.current = 'none';
    }
  }

  if (shouldRedirectState.current === 'redirect' && currentUser) {
    shouldRedirectState.current = 'redirecting';

    return <Navigate to={redirectToAfterSignIn} replace />;
  }
  return children;
};
